import { useState, type MouseEvent } from 'react'
import { Select } from '@/components/ui/input'
import {
  keysForFilter,
  type FactKeyPalette,
  type PaletteFilter,
} from '@/features/rules/fact-key-palette'
import { ANSWER_TYPE_LABELS } from '@/lib/constants'
import type { MaterialCategoryAttribute } from '@/lib/types'

/** Keeps focus (and the selection) in the text field when a chip is pressed. */
const keepFocus = (event: MouseEvent) => event.preventDefault()

/**
 * The fact-key palette above a logic box, as in the app: "Ключи (нажмите, чтобы вставить)", a group
 * filter (only when there is more than one bucket to choose from) and one chip per key showing the key
 * and its answer type. A chip inserts `{key}`. The list scrolls internally so it never pushes the
 * logic box out of view.
 */
export function KeyPalette({
  palette,
  onInsert,
}: {
  palette: FactKeyPalette
  onInsert: (token: string) => void
}) {
  const [filter, setFilter] = useState<PaletteFilter>(null)
  if (palette.keys.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        Доступных ключей пока нет: у вопросов этой области не задан ключ факта.
      </p>
    )
  }

  const hasSectionKeys = palette.keys.some((item) => item.groupId === '')
  const showFilter = palette.groups.length + (hasSectionKeys ? 1 : 0) > 1
  const visible = keysForFilter(palette.keys, filter)

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex-1 text-xs font-medium">Ключи (нажмите, чтобы вставить)</span>
        {showFilter ? (
          <Select
            value={filter ?? '__all__'}
            onChange={(event) =>
              setFilter(event.target.value === '__all__' ? null : event.target.value)
            }
            className="h-8 w-auto text-xs"
            aria-label="Фильтр ключей по группе"
          >
            <option value="__all__">Все группы</option>
            {hasSectionKeys ? <option value="">Вопросы секции</option> : null}
            {palette.groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </Select>
        ) : null}
      </div>
      <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto">
        {visible.map((item) => (
          <button
            key={item.key}
            type="button"
            onMouseDown={keepFocus}
            onClick={() => onInsert(`{${item.key}}`)}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/80 flex cursor-pointer flex-col items-start rounded-md px-2.5 py-1 text-left"
            title={`Вставить {${item.key}}`}
          >
            <span className="font-mono text-sm">{item.key}</span>
            <span className="text-muted-foreground text-[10px] leading-tight">
              {ANSWER_TYPE_LABELS[item.type]}
            </span>
          </button>
        ))}
        {visible.length === 0 ? (
          <span className="text-muted-foreground text-xs">В этой группе ключей нет.</span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Attribute chips of a material category (the question's for CHOICE / MULTI_CHOICE, the target
 * field's for REPEATER / QUESTION_LIST): each inserts `{attr:CODE}`, resolved at calculation time
 * from the material the user picked.
 */
export function AttributePalette({
  attributes,
  categoryName = '',
  onInsert,
}: {
  attributes: MaterialCategoryAttribute[]
  /** Shown in the heading so it is clear whose attributes these are. */
  categoryName?: string
  onInsert: (token: string) => void
}) {
  if (attributes.length === 0) return null
  return (
    <div className="grid gap-2">
      <span className="text-xs font-medium">
        Значения атрибутов{categoryName ? ` категории «${categoryName}»` : ''} (нажмите, чтобы
        вставить)
      </span>
      <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto">
        {attributes.map((attribute) => (
          <button
            key={attribute.id}
            type="button"
            onMouseDown={keepFocus}
            onClick={() => onInsert(`{attr:${attribute.code}}`)}
            className="bg-accent text-accent-foreground hover:bg-accent/80 flex cursor-pointer flex-col items-start rounded-md px-2.5 py-1 text-left"
            title={`Вставить {attr:${attribute.code}}`}
          >
            <span className="text-sm">{attribute.name}</span>
            <span className="text-muted-foreground font-mono text-[10px] leading-tight">
              {attribute.code}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
